import { ReactElement, useEffect, useState, useCallback } from 'react';
import ConnectWalletPopup from '@components/ConnectWalletPopup';
import NetworkToNetworkInfo from '@components/NetworkToNetworkInfo';
import ProgressBar from '@components/ProgressBar';
import TokenAssetSelection from '@components/TokenAssetSelection';
import useTransferAssetFlowContext from '@hooks/useTransferAssetFlowContext';
import useWallets from '@hooks/useWallets';
import { TransferAssetFlowStep } from '@providers/TransferAssetFlowProvider';
import BridgeTransactionView from './BridgeTransactionView';
import ConfirmTransferDetailsView from './ConfirmTransferDetailsView';
import HomePageCard from './HomePageCard';
import ProvideTransferDetailsView from './ProvideTransferDetailsView';
import useGenericPopup from '@hooks/useGenericPopup';
import MainLayout from '@components/MainLayout';
import useSendTransactionContext from '@hooks/useSendTransactionContext';
import { TxStatus } from '@types';
import { isBetaTx } from '@lib/utils';
import ExternalBridgeLinkContent from '@components/ExternalBridgeLinkContent';

function HomePageTransferAssetView(): ReactElement | null {
  const [hasOpenedAssetSelection, setHasOpenedAssetSelection] = useState(false);

  const { wallets } = useWallets();
  const { showWrongNetworkPopup, closePopup, popupType } = useGenericPopup();
  const {
    onHideAssetSelection,
    onHideExternalBridgeLink,
    onPrevStep,
    onTokenAssetChange,
    onValidate,
    state,
  } = useTransferAssetFlowContext();
  const { bridgeTxNotifications } = useSendTransactionContext();
  const latestBridgeTx =
    bridgeTxNotifications[bridgeTxNotifications?.length - 1]?.bridgeTx;

  const betaTx = isBetaTx(latestBridgeTx);

  const shouldRenderCheck = useCallback(
    (index: number): boolean => {
      const activeIndex = state.step - 1;
      return (
        // details and confirm step
        (activeIndex !== undefined && index < activeIndex) ||
        // bridge step
        (activeIndex === 2 &&
          (betaTx
            ? true
            : latestBridgeTx?.transferTx.status === TxStatus.fulfilled))
      );
    },
    [latestBridgeTx?.transferTx.status, state.step, betaTx]
  );

  useEffect(() => {
    if (
      hasOpenedAssetSelection &&
      state.step === TransferAssetFlowStep.ProvideDetails &&
      state.showAssetSelection === false
    ) {
      onValidate({
        fields: ['tokenAsset'],
        step: TransferAssetFlowStep.ProvideDetails,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOpenedAssetSelection, state.step, state.showAssetSelection]);

  useEffect(() => {
    if (wallets.length && wallets[0] && !wallets[0].active) {
      showWrongNetworkPopup();
    } else if (popupType === 'WRONG_NETWORK_ERROR') {
      closePopup();
    }
  }, [showWrongNetworkPopup, closePopup, wallets, popupType]);

  if (state.showWalletConnection) {
    return (
      <MainLayout variant="popup">
        <ConnectWalletPopup />
      </MainLayout>
    );
  }

  if (state.showAssetSelection) {
    return (
      <MainLayout>
        <HomePageCard
          title="Select Asset"
          onBack={(): void => {
            onHideAssetSelection();
            setHasOpenedAssetSelection(true);
          }}
        >
          <NetworkToNetworkInfo from={state.fromNetwork} to={state.toNetwork} />
          <TokenAssetSelection
            fromNetwork={state.fromNetwork}
            preSelectedTokenAsset={state.tokenAsset}
            toNetwork={state.toNetwork}
            onConfirm={(tokenAsset) => {
              onTokenAssetChange(tokenAsset);
              onHideAssetSelection();
              setHasOpenedAssetSelection(true);
            }}
          />
        </HomePageCard>
      </MainLayout>
    );
  }

  if (state.externalBridgeType) {
    return (
      <MainLayout>
        <HomePageCard
          title="Bridge via third party"
          onBack={onHideExternalBridgeLink}
        >
          <ExternalBridgeLinkContent type={state.externalBridgeType} />
        </HomePageCard>
      </MainLayout>
    );
  }

  if (state.step === TransferAssetFlowStep.ProvideDetails) {
    return (
      <MainLayout>
        <HomePageCard
          subTitle="The safest, fastest, and most secure way to transfer assets to and from Cronos EVM."
          title="Cronos EVM Bridge"
        >
          <div className="mx-auto w-1/2 mb-4">
            <ProgressBar
              activeIndex={state.step - 1}
              steps={['Details', 'Confirm', 'Bridge']}
              shouldRenderCheck={shouldRenderCheck}
            />
          </div>
          <ProvideTransferDetailsView />
        </HomePageCard>
      </MainLayout>
    );
  }

  if (state.step === TransferAssetFlowStep.ConfirmDetails) {
    return (
      <MainLayout>
        <HomePageCard title="Confirmation" onBack={onPrevStep}>
          <div className="mx-auto w-1/2 mb-4">
            <ProgressBar
              activeIndex={state.step - 1}
              steps={['Details', 'Confirm', 'Bridge']}
              shouldRenderCheck={shouldRenderCheck}
            />
          </div>
          <div className="mb-3">
            <ConfirmTransferDetailsView />
          </div>
        </HomePageCard>
      </MainLayout>
    );
  }

  if (state.step === TransferAssetFlowStep.BridgeTransaction) {
    return (
      <MainLayout>
        <HomePageCard
          title={betaTx ? 'Transaction Completed!' : 'Bridging Transaction'}
        >
          <div className="mx-auto w-1/2 mb-4">
            <ProgressBar
              activeIndex={state.step - 1}
              steps={['Details', 'Confirm', 'Bridge']}
              shouldRenderCheck={shouldRenderCheck}
            />
          </div>
          <div className="mb-3">
            <BridgeTransactionView />
          </div>
        </HomePageCard>
      </MainLayout>
    );
  }

  return null;
}

export default HomePageTransferAssetView;
